import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { fmtEUR, fmtDate, STATUSES } from "@/constants";
import StatusBadge from "@/components/StatusBadge";
import PdfImport from "@/components/PdfImport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const FORMULA_FILTERS = [
  ["all", "Toutes"],
  ["100", "100 photos"],
  ["200", "200 photos"],
  ["400", "400 photos"],
  ["700", "700 photos"],
  ["agb", "Audio Guest Book seul"],
  ["cabine", "Cabine AGB"],
];

const PERIOD_FILTERS = [
  ["semaine", "Cette semaine"],
  ["mois", "Ce mois"],
  ["mois_prochain", "Mois prochain"],
  ["toutes", "Toutes"],
];

const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const periodRange = (period) => {
  // Filtre côté serveur : ne charger que la période demandée (chargement plus rapide)
  const today = new Date();
  if (period === "semaine") {
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7)); // lundi de la semaine en cours
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6); // dimanche
    return { date_from: isoLocal(monday), date_to: isoLocal(sunday) };
  }
  if (period === "mois") {
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { date_from: isoLocal(new Date(today.getFullYear(), today.getMonth(), 1)), date_to: isoLocal(last) };
  }
  if (period === "mois_prochain") {
    // mois civil suivant ; le changement d'année est automatique (décembre → janvier)
    const first = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const last = new Date(today.getFullYear(), today.getMonth() + 2, 0);
    return { date_from: isoLocal(first), date_to: isoLocal(last) };
  }
  return {};
};

export default function Reservations() {
  const [items, setItems] = useState(null);
  const [boothNames, setBoothNames] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [formula, setFormula] = useState("all");
  const [period, setPeriod] = useState("semaine");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/settings").then((r) => setBoothNames(r.data.booth_names));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = { ...periodRange(period) };
      if (q) params.q = q;
      if (status) params.status = status;
      api.get("/reservations", { params }).then((r) => setItems(r.data));
    }, 200);
    return () => clearTimeout(t);
  }, [q, status, period]);

  const matchFormula = (r) => {
    if (formula === "all") return true;
    const kind = r.kind || "photobooth";
    if (formula === "agb") return kind === "agb";
    if (formula === "cabine") return kind === "cabine";
    return kind === "photobooth" && (r.pricing?.formula || "") === formula;
  };
  const filtered = items ? items.filter((r) => matchFormula(r)) : null;

  return (
    <div className="space-y-5" data-testid="reservations-page">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Réservations</h1>
        <Button data-testid="btn-new-booking-list" onClick={() => navigate("/reservations/nouvelle")} className="bg-slate-900 hover:bg-slate-800 text-white transition-colors">
          <Plus className="h-4 w-4 mr-1" /> Nouvelle
        </Button>
      </div>

      <PdfImport />

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="reservations-search-input"
            placeholder="Rechercher un client ou une ville…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
        <select
          data-testid="reservations-status-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-10 rounded-md border border-input bg-white px-3 text-sm"
        >
          <option value="">Tous les statuts</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="reservations-formula-tabs">
        {FORMULA_FILTERS.map(([v, label]) => (
          <button
            key={v}
            data-testid={`reservations-formula-tab-${v}`}
            onClick={() => setFormula(v)}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${formula === v ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white text-slate-600 hover:border-sky-400"}`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="flex gap-1.5" data-testid="reservations-period-tabs">
        {PERIOD_FILTERS.map(([v, label]) => (
          <button
            key={v}
            data-testid={`reservations-period-${v}`}
            onClick={() => setPeriod(v)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${period === v ? "bg-sky-500 text-white border-sky-500" : "border-slate-200 bg-white text-slate-600 hover:border-sky-400"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {!filtered ? (
        <p className="text-slate-400">Chargement…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="reservations-empty">
          <p className="text-slate-500">Aucune réservation sur cette période.</p>
          <Button data-testid="btn-new-booking-empty" onClick={() => navigate("/reservations/nouvelle")} className="mt-4 bg-sky-500 hover:bg-sky-600 text-white transition-colors">
            <Plus className="h-4 w-4 mr-1" /> Créer ma première réservation
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="reservations-results">
          {filtered.map((r) => (
            <button
              key={r.id}
              data-testid={`reservation-card-${r.id}`}
              onClick={() => navigate(`/reservations/${r.id}`)}
              className="text-left rounded-2xl border border-slate-200 bg-white p-4 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] duration-200"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-heading font-semibold truncate">{r.client?.name}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                    <MapPin className="h-3 w-3 shrink-0" /> {r.event?.city} · {r.event?.type === "Autre" && r.event?.type_other ? r.event.type_other : r.event?.type}
                  </p>
                </div>
                <StatusBadge status={r.status} />
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-slate-600">{fmtDate(r.event?.date)}{r.logistics?.booth_id !== null && r.logistics?.booth_id !== undefined && r.logistics?.booth_id !== "" ? ` · ${boothNames[r.logistics.booth_id] || ""}` : ""}</span>
                <span className="font-mono tabular-nums font-semibold">{fmtEUR(r.pricing?.total)}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
